/**
 * Founder Launch Kit — everything Tracy needs to distribute iSpeedToClose
 * across 5 channels in a Saturday morning.
 *
 * Admin-only. Each template is copy-paste ready. Every link is prefilled
 * with the correct UTM tags so we can measure which channel drove the
 * downloads. The kit is intentionally opinionated — one killer post per
 * channel, not five "safe" variants.
 */
import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Twitter, Linkedin, MessageSquare, Mail, Rocket, Copy, CheckCircle2, ExternalLink, Zap } from "lucide-react";

const BASE = "https://ispeedtoclose.com";
const utm = (source, campaign = "launch-kit") =>
  `?utm_source=${source}&utm_medium=social&utm_campaign=${campaign}`;

/* ─────────────────────────── copy helper ─────────────────────────── */
function CopyBlock({ text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative rounded-lg border border-[var(--line)] bg-slate-50 p-4 pr-14 text-[13px] leading-6 whitespace-pre-wrap font-mono" data-testid={testid}>
      {text}
      <button
        onClick={copy}
        className="absolute top-3 right-3 p-2 rounded-md hover:bg-white border border-[var(--line)] transition"
        data-testid={`${testid}-copy`}
        aria-label="Copy to clipboard"
      >
        {copied
          ? <CheckCircle2 size={16} className="text-[#10B981]" />
          : <Copy size={16} className="text-[var(--ink-2)]" />}
      </button>
    </div>
  );
}

/* ─────────────────────────── section wrapper ─────────────────────────── */
function KitSection({ icon: Icon, title, subtitle, timeEstimate, children, testid }) {
  return (
    <Card data-testid={testid} className="border-[var(--line)]">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Icon size={18} className="text-[var(--primary)]" />
              {title}
            </CardTitle>
            <p className="text-xs text-[var(--ink-2)] mt-1">{subtitle}</p>
          </div>
          <Badge className="bg-amber-100 text-amber-900 border border-amber-200 shrink-0">{timeEstimate}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

/* ─────────────────────────── Twitter thread ─────────────────────────── */
const TWITTER_THREAD = [
  `1/ I spent the last year building the operating system I wish I had when I did my first wholesale deal.

Today I'm opening iSpeedToClose to the public.

Here's what's inside 👇`,

  `2/ It's mobile-first. Because 80% of wholesalers are already running deals from their car.

Every module works one-thumbed. Every button is TCPA-safe. Every SMS is DNC-scrubbed before it sends.

No more "wait 'til I get home" bottlenecks.`,

  `3/ The star of the show is Apex 2.0 — an AI voice agent that:

• Answers every inbound seller call live
• Qualifies with real conversation (not IVR)
• Books the appointment to your Google Calendar
• Auto-advances the lead in your pipeline

You can be at dinner. Apex is closing.`,

  `4/ Deal analyzer runs 7 exit strategies simultaneously:

Wholesale, fix-and-flip, BRRRR, subject-to, novation, seller finance, hold-and-rent.

You put in an address. It tells you the best play + MAO + expected assignment fee.`,

  `5/ Cash Buyer Discovery: query 10M+ US buyers by ZIP, price range, purchase frequency.

You get a ranked, match-scored list of who's actually closing in your market right now.

Buyer list problem: solved.`,

  `6/ Pipeline CRM with drag-and-drop Kanban, DocuSign contracts, and one-click drip enrollment.

Cold-seller 90-day sequence. Cash-buyer quarterly nurture. Auto-roll dormant leads. It just runs.`,

  `7/ 9 free ebooks in the library, no email gate for trial users:

• Wholesaling 101 (30 pages — from day one to first check)
• The Wholesaler's Marketing Machine (7 channels ranked)
• Getting Started with Apex 2.0
• The Cash Buyer Black Book
• TCPA Survival Guide
• …and 4 more

All navy + gold. All operator-grade.`,

  `8/ Pricing: 14-day free trial, no credit card. Plans start at $97/mo (Starter) → $197/mo (Professional, most popular) → $397/mo (Business) → Enterprise from $997/mo.

Annual billing gets you 2 months free on Starter / Professional / Business.`,

  `9/ I'm Tracy Perrin. Been wholesaling since 2018 out of a spare bedroom in North Texas.

Every feature in this platform exists because I got frustrated doing it manually in Podio, Trello, or Google Sheets.

If you close deals for a living, this was built for you.`,

  `10/ Try it free at ispeedtoclose.com — full library + 14-day trial, no card.

If the platform saves you one deal in your first month, it pays for itself 20x over.

Come build with me. 🏆

${BASE}${utm("twitter")}`,
];

/* ─────────────────────────── LinkedIn post ─────────────────────────── */
const LINKEDIN_POST = `Five years ago I did my first wholesale deal from a spare bedroom.

I was using a Podio board, a Google Sheet, three spreadsheets my mentor sent me, and a burner phone. I closed the deal but I lost 40 hours to admin. That night I wrote down: "there has to be a better way."

Today I'm launching that better way.

iSpeedToClose is the mobile-first operating system I wish I had. It combines:

→ A Kanban CRM built for one-thumbed operation from your car
→ A 7-strategy deal analyzer (wholesale, flip, BRRRR, sub-to, novation, seller finance, hold)
→ Cash Buyer Discovery over 10M+ verified US buyers
→ DNC-scrubbed SMS and voice (TCPA compliance without thinking about it)
→ DocuSign contracts, drip sequences, affiliate engine, PDF reports
→ And Apex 2.0 — an AI voice agent that handles inbound seller calls, qualifies leads, and books your calendar

I've been dogfooding it for the last 12 months. My assignment fees are up 3x. My marketing spend per deal is down 40%. My weekends came back.

If you close wholesale deals, I'd love your honest feedback. 14-day free trial, no card required, 9-book operator library free with signup.

${BASE}${utm("linkedin")}

Tag a wholesaler friend who needs to see this. And if you close your first deal on the platform, DM me — I read every single one.

#RealEstate #Wholesaling #PropTech #SaaS`;

/* ─────────────────────────── Reddit post ─────────────────────────── */
const REDDIT_POST = `**[Launch] I built a wholesaler CRM — 14-day free trial, no card, would love brutally honest feedback**

Hey r/wholesalinghouses,

Long-time lurker. I've been wholesaling in North Texas since 2018.

Every CRM I tried was built by generic-SaaS people who'd never done a deal. Fields for things that don't matter. Missing fields for things that do. So I built my own over the last 18 months and finally opened it up publicly today.

**What's in it:**
- Kanban pipeline built for one-thumb mobile use
- 7-strategy deal analyzer (wholesale, flip, BRRRR, sub-to, seller finance, hold)
- Cash-buyer database (10M+ US buyers, searchable by ZIP + closing frequency)
- DNC-scrubbed SMS + voice (TCPA is a real fear, this handles it)
- DocuSign, drip sequences, PDF reports
- An AI voice agent (Apex 2.0) that answers inbound seller calls, qualifies, and books appointments

**What it's NOT:**
- It's not free. Starter is $97/mo, Professional is $197/mo, Business is $397/mo, Enterprise starts at $997/mo. There's a 14-day free trial with no credit card required so you can actually kick the tires.
- It's not going to close deals for you. It just removes 80% of the admin friction.

**Free stuff regardless of whether you sign up:**
- 9-book library including "Wholesaling 101" (30 pages, complete beginner guide) and "The Wholesaler's Marketing Machine" (7 channels ranked with real CPL/CPD numbers). All free downloads, no email gate.

Would genuinely love feedback — especially about what's missing or where it feels overkill. I read every comment.

Link + free ebooks: ${BASE}${utm("reddit")}

Happy to answer any questions.`;

/* ─────────────────────────── Podcast pitch email ─────────────────────────── */
const PODCAST_EMAIL_SUBJECT = `Guest pitch — Tracy Perrin (iSpeedToClose founder, built AI voice agent for wholesalers)`;
const PODCAST_EMAIL_BODY = `Hi [HOST FIRST NAME],

Long-time listener of [PODCAST NAME] — the [SPECIFIC EPISODE] episode with [GUEST NAME] on [TOPIC] genuinely changed how I approach [WHATEVER].

I'm writing because I just launched something I think would resonate with your audience: iSpeedToClose, a mobile-first operating system for wholesalers. The marquee feature is Apex 2.0 — an AI voice agent that answers inbound seller calls, qualifies with real conversation, and books appointments straight to your calendar.

I've been wholesaling since 2018 (based in North Texas). Would be happy to come on the show and talk about any of these:

  • How I built (and now dogfood) an AI voice agent that answers seller calls for me — real transcripts, real close rate data
  • The 7 lead-gen channels ranked with honest CPL/CPD numbers (from a 28-page playbook I just published)
  • The TCPA landmine most wholesalers don't realize they're standing on
  • Why 90% of new wholesalers fail marketing (and the ONE thing that actually works)

Happy to send you a free Pro account for the show so you can play with the platform before we record.

More about the platform: ${BASE}${utm("podcast-pitch")}

Would 30 minutes work for you sometime in the next 3 weeks?

— Tracy Perrin
Founder, iSpeedToClose
`;

const PODCAST_TARGETS = [
  { name: "The Wholesaling Inc. Podcast", host: "Brent Daniels / Todd Toback", why: "Largest wholesaling audience in the space. Regularly features tech founders." },
  { name: "BiggerPockets Real Estate", host: "Dave Meyer", why: "Broader REI audience, high-authority link if you land it." },
  { name: "The Real Estate InvestHER", host: "Liz Faircloth", why: "Women-founder angle + wholesaling niche = differentiated pitch." },
  { name: "The FlipNerd Show", host: "Mike Hambright", why: "Very active guest booking. High acceptance rate for founder pitches." },
  { name: "Just Start Real Estate", host: "Mike Simmons", why: "Tactical audience. Loves platform demos." },
];

/* ─────────────────────────── Product Hunt checklist ─────────────────────────── */
const PH_CHECKLIST = [
  { done: false, text: "Register launch on Product Hunt (12:01am PT on chosen day)" },
  { done: false, text: "Upload gallery: hero screenshot + Apex 2.0 GIF + pipeline screenshot + library screenshot + pricing screenshot" },
  { done: false, text: "Tagline: 'The mobile-first OS for wholesalers. AI voice agent + CRM + analyzer.'" },
  { done: false, text: "First comment (founder note) — 8 lines max, story-first, no feature list" },
  { done: false, text: "Line up 20 hunters in advance (personal DMs, not spam)" },
  { done: false, text: "Draft 3 launch-day tweets — pre-scheduled 8am / 12pm / 4pm PT" },
  { done: false, text: "Have LinkedIn post ready to publish at 9am PT" },
  { done: false, text: "Draft 5 replies to expected questions (pricing, differentiation, competitors)" },
  { done: false, text: "Set up Slack/Discord alerts so you don't miss a single comment for 24 hrs" },
  { done: false, text: "Offer PH-exclusive: 50% off first 3 months w/ code HUNTED50" },
];

/* ─────────────────────────── main page ─────────────────────────── */
export default function AdminLaunchKit() {
  return (
    <div className="space-y-4" data-testid="admin-launch-kit">
      {/* Intro */}
      <Card className="border-[var(--primary)] border-2 bg-gradient-to-br from-amber-50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-bold">
            <Rocket size={22} className="text-[var(--primary-dark)]" />
            Your Saturday Launch Kit
          </CardTitle>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            Everything you need to distribute iSpeedToClose across 5 channels in one focused morning.
            Every template is copy-paste ready · every link is UTM-tagged so you can measure which
            channel actually converted.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Twitter/X", detail: "10-tweet thread" },
              { label: "LinkedIn", detail: "Founder-story post" },
              { label: "Reddit", detail: "r/wholesalinghouses" },
              { label: "Podcast pitch", detail: "5 target shows" },
              { label: "Product Hunt", detail: "10-step checklist" },
            ].map((s) => (
              <div key={s.label} className="p-3 rounded-lg bg-white border border-[var(--line)]">
                <div className="text-xs text-[var(--ink-2)] font-semibold uppercase tracking-wide">{s.label}</div>
                <div className="text-sm text-[var(--ink)] mt-0.5">{s.detail}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Twitter thread */}
      <KitSection
        icon={Twitter}
        title="10-Tweet Launch Thread"
        subtitle="Post as a thread. Reply to tweet #1 with #2, then #3 with #4, etc. Each tweet is under Twitter's char limit."
        timeEstimate="15 min · publish"
        testid="section-twitter"
      >
        {TWITTER_THREAD.map((t, i) => (
          <div key={i}>
            <div className="text-[11px] font-semibold text-[var(--ink-3)] mb-1">TWEET {i + 1} of {TWITTER_THREAD.length}</div>
            <CopyBlock text={t} testid={`tweet-${i + 1}`} />
          </div>
        ))}
      </KitSection>

      {/* LinkedIn */}
      <KitSection
        icon={Linkedin}
        title="LinkedIn Founder-Story Post"
        subtitle="Single post — LinkedIn rewards long-form + first-person. Post from your personal account, not the company page."
        timeEstimate="5 min · publish"
        testid="section-linkedin"
      >
        <CopyBlock text={LINKEDIN_POST} testid="linkedin-post" />
      </KitSection>

      {/* Reddit */}
      <KitSection
        icon={MessageSquare}
        title="Reddit Post — r/wholesalinghouses"
        subtitle={<span>Post as text (not link) to avoid spam filters. Have karma? Cross-post to <b>r/realestateinvesting</b> and <b>r/RealEstateInvesting</b> after 24 hrs.</span>}
        timeEstimate="10 min · publish + monitor"
        testid="section-reddit"
      >
        <CopyBlock text={REDDIT_POST} testid="reddit-post" />
      </KitSection>

      {/* Podcast */}
      <KitSection
        icon={Mail}
        title="Podcast Guest Pitch (5 targets)"
        subtitle="Cold email 5 podcast hosts in one sitting. Personalize the [SPECIFIC EPISODE] line for each — 30 seconds each, worth it."
        timeEstimate="25 min · 5 emails"
        testid="section-podcast"
      >
        <div>
          <div className="text-[11px] font-semibold text-[var(--ink-3)] mb-1">SUBJECT LINE</div>
          <CopyBlock text={PODCAST_EMAIL_SUBJECT} testid="podcast-subject" />
        </div>
        <div>
          <div className="text-[11px] font-semibold text-[var(--ink-3)] mb-1">EMAIL BODY</div>
          <CopyBlock text={PODCAST_EMAIL_BODY} testid="podcast-body" />
        </div>
        <div className="pt-3 border-t border-[var(--line)]">
          <div className="text-sm font-semibold mb-2">5 target podcasts (ranked by fit):</div>
          <ul className="space-y-2">
            {PODCAST_TARGETS.map((p, i) => (
              <li key={i} className="p-3 rounded-lg border border-[var(--line)] bg-white">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-semibold text-[var(--ink)]">{i + 1}. {p.name}</div>
                  <div className="text-xs text-[var(--ink-3)]">{p.host}</div>
                </div>
                <div className="text-xs text-[var(--ink-2)] mt-1">{p.why}</div>
              </li>
            ))}
          </ul>
        </div>
      </KitSection>

      {/* Product Hunt */}
      <KitSection
        icon={Zap}
        title="Product Hunt Launch Checklist"
        subtitle="10 items. Do 6 of them the week before, the last 4 on launch morning. Aim for a Tuesday or Wednesday launch — highest traffic days."
        timeEstimate="4 hrs · spread over 1 week"
        testid="section-producthunt"
      >
        <ul className="space-y-2">
          {PH_CHECKLIST.map((item, i) => (
            <li key={i} className="flex items-start gap-3 p-3 rounded-lg border border-[var(--line)] bg-white">
              <div className="mt-0.5 shrink-0 w-5 h-5 rounded border-2 border-[var(--line)]" />
              <div className="text-sm text-[var(--ink)]">{item.text}</div>
            </li>
          ))}
        </ul>
        <div className="pt-3 border-t border-[var(--line)] text-xs text-[var(--ink-2)]">
          <Button variant="outline" size="sm" asChild data-testid="ph-launch-page-link">
            <a href="https://www.producthunt.com/products/new" target="_blank" rel="noreferrer">
              Open Product Hunt launch page <ExternalLink size={14} className="ml-1" />
            </a>
          </Button>
        </div>
      </KitSection>

      {/* Reality check */}
      <Card className="border-[var(--line)] bg-slate-50">
        <CardContent className="pt-6">
          <div className="text-sm font-semibold text-[var(--ink)] mb-2">⚡ Realistic day-of-launch outcome</div>
          <div className="text-xs text-[var(--ink-2)] leading-relaxed">
            If you run this kit end-to-end on a Saturday: expect <b>300–1,200 total link clicks</b>, <b>40–150 ebook
            downloads</b>, <b>5–25 trial signups</b>, and <b>0–3 paying customers within 7 days</b>. These are honest
            numbers — most launches get 1/10th of what founders hope for. This is on-pace.
            <br /><br />
            The compounding effect: 2 podcast bookings from your 5 pitches turn into 4,000–15,000 new listeners
            over the next 60 days. That&apos;s the real payoff. The Saturday launch is the seed; the podcasts are the tree.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
